import { type ConversationAction } from '@atlassian/rovo-conversation-actions/types';

import { fg } from '@confluence/feature-gating';

import type { AutomationNudgeContent } from '../hooks/useConfluenceAutomationNudgeActions.tsx';
import { useExistingStudioRules } from '../hooks/useExistingStudioRules.ts';
import { existingContentReviewerRuleExists } from '../hooks/studioRuleMatchers.ts';

import { useContentReviewerNudgeAction } from './useContentReviewerNudgeAction.tsx';
import { useContentReviewerNudgeOnCriteria } from './useContentReviewerNudgeOnCriteria.tsx';
import { useContentReviewerNudgeOnPublishSignal } from './useContentReviewerNudgeOnPublishSignal.ts';

interface UseContentReviewerNudgeProps {
	/** The settled, reactive `singleContent` slice from the parent query. */
	content?: AutomationNudgeContent | null;
	/** The current content id, used to detect the OnPublish event trigger for this page. */
	contentId: string;
}

/**
 * Entrypoint for the Content Reviewer nudge. Returns the shared action when either trigger fires:
 *  - the in-editor criteria match (content-state transition into "Ready for review" + word count —
 *    see `useContentReviewerNudgeOnCriteria`), or
 *  - the OnPublish signal fires for this content (the page was just published — see
 *    `useContentReviewerNudgeOnPublishSignal`).
 *
 * Both triggers resolve to the same action via `useContentReviewerNudgeAction`, so they share one
 * action id, experiment gate, and dismissal bookkeeping.
 *
 * When the user already has a matching Content Reviewer automation in Studio, the nudge is
 * suppressed (returns `null`). The rules fetch — and the rollout gate's exposure event — is deferred
 * until a trigger actually fires, so users who never match neither fetch nor get exposed.
 */
export const useContentReviewerNudge = ({
	content,
	contentId,
}: UseContentReviewerNudgeProps): ConversationAction | null => {
	const matchesCriteria = useContentReviewerNudgeOnCriteria({ content });
	const hasOnPublishSignal = useContentReviewerNudgeOnPublishSignal(contentId);
	const isCandidate = matchesCriteria || hasOnPublishSignal;

	const { rules, isLoading, error } = useExistingStudioRules({
		skip: !isCandidate || !fg('automations-integration-to-rovo-button-nudges'),
	});

	const hasCreatedContentReviewerAutomation = existingContentReviewerRuleExists(rules);

	// Fail closed: only surface the nudge once we've confirmed it is relevant — i.e. the rules have
	// loaded without error and the user does not already have a matching automation. If the fetch is
	// in flight or errors we can't confirm relevance, so we withhold (and, since `isEnabled` is then
	// false, avoid exposing the experiment) rather than risk nudging someone who already automated.
	return useContentReviewerNudgeAction(
		isCandidate && !isLoading && !error && !hasCreatedContentReviewerAutomation,
	);
};
